import React from 'react';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from './ui/accordion';
import { HelpCircle, FileText } from 'lucide-react';

const faqs = [
  {
    id: 'item-1',
    question: 'How long does a typical collision repair take?',
    answer: 'The timeline varies depending on the extent of damage. Minor repairs typically take 2-3 days, while more extensive collision damage may require 1-2 weeks. We provide a detailed estimate with expected completion time during your initial consultation and keep you updated throughout the process.'
  },
  {
    id: 'item-2',
    question: 'Will the paint match my car\'s original color?',
    answer: 'Absolutely! We use advanced color-matching technology to ensure a perfect match with your vehicle\'s original paint. Our experienced technicians blend the paint seamlessly, so you won\'t be able to tell where the repair was made.'
  },
  {
    id: 'item-3',
    question: 'Do you provide free estimates?',
    answer: 'Yes, we offer free, no-obligation estimates for all repair services. Simply bring your vehicle to our shop or contact us to schedule an appointment. We\'ll thoroughly inspect the damage and provide a detailed written estimate.'
  },
  {
    id: 'item-4',
    question: 'What is your warranty policy?',
    answer: 'We stand behind our work with a comprehensive warranty on all repairs. The specific warranty terms depend on the type of service performed. We\'ll provide full warranty details with your estimate and ensure you\'re completely satisfied with our work.'
  },
  {
    id: 'item-5',
    question: 'What should I do immediately after an accident?',
    answer: 'First, ensure everyone\'s safety and call emergency services if needed. Document the accident with photos, exchange information with other parties involved. Then, bring your vehicle to us for a professional assessment and repair estimate.'
  },
  {
    id: 'item-6',
    question: 'Do I need an appointment or can I walk in?',
    answer: 'While we welcome walk-ins, we recommend calling ahead to schedule an appointment. This ensures one of our technicians is available to give your vehicle the attention it deserves and provide you with a thorough estimate.'
  },
  {
    id: 'item-7',
    question: 'What types of payment do you accept?',
    answer: 'We accept cash, credit cards, and debit cards. Payment is due upon completion of the work. We\'ll discuss all costs upfront in your estimate so there are no surprises.'
  },
  {
    id: 'item-8',
    question: 'Are you certified and licensed?',
    answer: 'Yes, Kingsway Auto Collision is fully licensed and insured. Our technicians are highly trained professionals with years of experience in auto body repair. We use industry-standard practices and high-quality materials for all our work.'
  },
  {
    id: 'item-9',
    question: 'What areas do you serve?',
    answer: 'We are located in Burnaby, BC and proudly serve customers throughout the Greater Vancouver area including Burnaby, Vancouver, New Westminster, Richmond, and surrounding communities.'
  },
  {
    id: 'item-10',
    question: 'Can you repair all makes and models?',
    answer: 'Yes, we have the expertise and equipment to repair all makes and models of vehicles, from domestic to foreign, sedans to SUVs. Our experienced technicians work on all types of vehicles with equal care and precision.'
  }
];

export const FAQ = () => {
  return (
    <section className="py-20 bg-slate-50">
      <div className="max-w-4xl mx-auto px-6">
        {/* Section Header */}
        <div className="text-center mb-12">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-amber-100 rounded-full mb-4">
            <HelpCircle className="h-8 w-8 text-amber-600" />
          </div>
          <h2 className="text-4xl md:text-5xl font-bold text-slate-900 mb-4">
            Frequently Asked Questions
          </h2>
          <p className="text-xl text-slate-600">
            Everything you need to know about our auto body repair services
          </p>
        </div>

        {/* FAQ Accordion */}
        <Accordion type="single" collapsible className="space-y-4">
          {faqs.map((faq) => (
            <AccordionItem 
              key={faq.id} 
              value={faq.id}
              className="bg-white rounded-lg border border-slate-200 px-6 shadow-sm hover:shadow-md transition-shadow"
            >
              <AccordionTrigger className="text-left font-semibold text-slate-900 hover:text-amber-600 transition-colors py-5">
                {faq.question}
              </AccordionTrigger>
              <AccordionContent className="text-slate-600 leading-relaxed pb-5">
                {faq.answer}
              </AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>

        {/* Contact CTA */}
        <div className="text-center mt-12 p-8 bg-white rounded-xl border border-slate-200">
          <p className="text-lg text-slate-700 mb-4">
            Still have questions? We're here to help!
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a href="tel:6045680063" className="inline-flex items-center justify-center px-6 py-3 bg-amber-600 hover:bg-amber-700 text-white font-semibold rounded-lg transition-colors">
              Call Us: 604-568-0063
            </a>
            <button 
              onClick={() => document.getElementById('contact')?.scrollIntoView({ behavior: 'smooth' })}
              className="inline-flex items-center justify-center px-6 py-3 bg-slate-900 hover:bg-slate-800 text-white font-semibold rounded-lg transition-colors"
            >
              <FileText className="mr-2 h-4 w-4" />
              Get FREE Estimate
            </button>
          </div>
        </div>
      </div>
    </section>
  );
};

export default FAQ;
